import { SECTION_LEVEL_PROP } from '@app/src/hooks/getAssessmentFilterLabel'
import type { RequestAutomation } from '@app/src/types/automations'
import type { FilterValue } from '@app/src/types/filter'
import type { AssessmentLevelNamesType, AssessmentTemplate, AssessmentType } from '@app/src/types/resourceExplorer'
import { getFilterOptionsForLevels, LabelFormat } from '@app/src/utils/assessmentLevelTranslations'
import type { IntlFormatters, IntlShape } from 'react-intl'
import { RequestAutomationType } from './RequestAutomationType'

export const getLevelLabels = (
  levelValues: string[] | string,
  assessmentType: AssessmentType,
  levels: number,
  levelNamesType: AssessmentLevelNamesType | undefined,
  formatMessage: IntlFormatters['formatMessage'],
): string => {
  const options = getFilterOptionsForLevels(assessmentType, levels, formatMessage, levelNamesType, LabelFormat.Basic)
  const values = Array.isArray(levelValues) ? levelValues : [levelValues]

  return values
    .map(v => options.find(opt => String(opt.value) === String(v))?.label)
    .filter((l): l is string => !!l)
    .join(', ')
}

export const getSectionLevels = (
  filters: RequestAutomation['categoryFilters'],
  template: AssessmentTemplate,
  formatMessage: IntlFormatters['formatMessage'],
) =>
  filters
    ?.filter(f => f.name.startsWith(SECTION_LEVEL_PROP))
    .map(f => {
      const sectionNum = Number(f.name.match(/section(\d+)Level/)?.[1])
      const section = template.sections?.find(s => s.assessmentSectionNumber === sectionNum)

      const rawValue = f.filters?.[0]?.value
      const value = Array.isArray(rawValue)
        ? rawValue.map(v => v.toString())
        : rawValue !== undefined
          ? [rawValue.toString()]
          : []

      return {
        sectionName: section?.name ?? `Section ${sectionNum}`,
        level: getLevelLabels(
          value,
          template.assessmentType,
          template.levels ?? 5,
          template.levelNamesType,
          formatMessage,
        ),
      }
    }) ?? []

export const getTotalLevelLabel = (
  value: FilterValue | undefined,
  template: AssessmentTemplate,
  formatMessage: IntlFormatters['formatMessage'],
) => {
  const levelValues = Array.isArray(value)
    ? value.map(v => v.toString()).sort((a, b) => Number(a) - Number(b))
    : value !== undefined
      ? [value.toString()]
      : []

  return getLevelLabels(
    levelValues,
    template.assessmentType,
    template.levels ?? 5,
    template.levelNamesType,
    formatMessage,
  )
}

export interface AutomationVisibilityConfig {
  showAiScreen: boolean
  showAiEnrich: boolean
  showAssessmentLevel: boolean
  disableAiEnrich: boolean
}

/**
 * Pure function to determine which automation types should be visible/enabled
 * based on organization settings, permissions, and existing automations.
 */
export function computeAutomationVisibility(
  isAiScreeningEnabled: boolean,
  canAccessAssessmentTemplate: boolean,
  hasEnabledAssessments: boolean,
  existingAutomations: RequestAutomation[],
): AutomationVisibilityConfig {
  const hasAiEnrichAutomation = existingAutomations.some(
    automation => automation.automationType === RequestAutomationType.AI_ENRICH,
  )

  return {
    showAiScreen: isAiScreeningEnabled,
    showAiEnrich: true,
    showAssessmentLevel: canAccessAssessmentTemplate || hasEnabledAssessments,
    disableAiEnrich: hasAiEnrichAutomation,
  }
}

/**
 * Pure function to filter enabled assessment settings
 */
export function filterEnabledAssessments<T extends { isActivated: boolean }>(assessments: T[] | undefined): T[] {
  return assessments?.filter(setting => setting.isActivated) ?? []
}

/**
 * Pure function to build automation payload, conditionally including fields based on automation type.
 * Prevents sending invalid empty arrays/nulls for automation types that don't need certain fields.
 */
export function buildAutomationPayload(
  automationType: RequestAutomationType,
  formData: {
    id?: number
    templateIds: number[]
    periodStartsAt?: string | null
    periodEndsAt?: string | null
    deadlineOffsetDays?: number | null
    customDueDate?: string | null
    categoryFilters?: RequestAutomation['categoryFilters']
    applyToExisting: boolean
  },
) {
  // Base payload for all automation types
  const payload: Record<string, unknown> = {
    automationType,
    applyToExisting: formData.applyToExisting,
    isActive: true,
  }

  if (formData.id) {
    payload.id = formData.id
  }

  // Add fields conditionally based on automation type
  switch (automationType) {
    case RequestAutomationType.AI_ENRICH:
    case RequestAutomationType.EXPECTATION:
      // These types don't require any additional fields
      break

    case RequestAutomationType.AI_SCREEN:
      // Only requires templates
      payload.templateIds = formData.templateIds
      break

    case RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY:
    case RequestAutomationType.FOR_EVERY_NEW_PROVIDER:
    case RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL:
    default:
      // Standard automation types require templates, periods, and deadlines
      payload.templateIds = formData.templateIds
      payload.periodStartsAt = formData.periodStartsAt
      payload.periodEndsAt = formData.periodEndsAt
      payload.deadlineOffsetDays = formData.deadlineOffsetDays
      payload.customDueDate = formData.customDueDate
      payload.categoryFilters = formData.categoryFilters
      break
  }

  return payload
}

/**
 * Function to determine if applyToExisting selection is required for an automation type
 */
export function requiresApplyToExistingSelection(automationType: RequestAutomationType): boolean {
  return (
    automationType === RequestAutomationType.AI_ENRICH ||
    automationType === RequestAutomationType.AI_SCREEN ||
    automationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY ||
    automationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL ||
    automationType === RequestAutomationType.EXPECTATION
  )
}

export function getAutomationSummaryLabel(
  automationType: RequestAutomationType,
  intl: IntlShape,
  templateTitle?: string,
  totalLevelLabel?: string,
  sectionLevels?: Array<{ sectionName: string; level: string }>,
  riskFilters?: Array<{ type: string; indexLabel: string; riskLabel: string }>,
  assessmentCategoryText?: string,
  sourceFilterValue?: string,
): string {
  const formatMessage = intl.formatMessage

  if (automationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL) {
    const andJoiner = formatMessage({ id: 'automatedSendout.automationSpecifications.andJoiner' })
    const sectionPrefix = formatMessage({ id: 'automatedSendout.automationSpecifications.sectionPrefix' })

    const sectionText =
      sectionLevels
        ?.map(s =>
          formatMessage(
            { id: 'automatedSendout.automationSpecifications.sectionCondition' },
            { sectionName: s.sectionName, sectionLevel: s.level },
          ),
        )
        .join(andJoiner) || ''

    const fullSectionText = sectionText ? `${andJoiner}${sectionPrefix} ${sectionText}` : ''
    const levelPart = totalLevelLabel ? ` = '${totalLevelLabel}'` : ''

    const sourceFilterText = sourceFilterValue
      ? ` ${andJoiner}${formatMessage({ id: 'form.createAutomation.automationType.sourceLabel' })} '${sourceFilterValue}'`
      : ''
    const riskFilterText =
      riskFilters && riskFilters.length > 0
        ? `${andJoiner}${riskFilters
            .map(
              rf => `${formatMessage({ id: `schemas.risk.${rf.type}RiskIndex` })}: ${rf.indexLabel} = ${rf.riskLabel}`,
            )
            .join(andJoiner)}`
        : ''

    const categoryText = assessmentCategoryText || ''

    return formatMessage(
      { id: 'automatedSendout.automationSpecifications.whenAssessmentLevelChangesDetailed' },
      {
        templateTitle,
        levelPart,
        sectionText: fullSectionText,
        riskStatus: riskFilterText,
        categoryText,
        sourceFilterValue: sourceFilterText,
        sendLabel: formatMessage({ id: 'automatedSendout.sendQuestionnaire' }),
      },
    )
  }

  if (automationType === RequestAutomationType.AI_ENRICH) {
    return formatMessage({ id: 'automatedSendout.automationSpecifications.whenCompanyImportedOrAdded' })
  }

  const fallbackId =
    automationType === RequestAutomationType.AI_SCREEN
      ? 'automatedSendout.automationSpecifications.whenCompanyImportedOrAdded'
      : 'automatedSendout.automationSpecifications.forEveryNewProvider'

  return formatMessage({ id: fallbackId })
}

export function getCompleteAutomationSummary(
  automationType: RequestAutomationType,
  intl: IntlShape,
  templateTitle?: string,
  totalLevelLabel?: string,
  sectionLevels?: Array<{ sectionName: string; level: string }>,
  riskFilters?: Array<{ type: string; indexLabel: string; riskLabel: string }>,
  assessmentCategoryText?: string,
  sourceFilterValue?: string,
  categoryLabel?: string,
): string {
  const formatMessage = intl.formatMessage

  if (automationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY) {
    const action = formatMessage({ id: 'automatedSendout.sendQuestionnaire' })
    return categoryLabel ? `${categoryLabel} → ${action}` : `→ ${action}`
  }

  const trigger = getAutomationSummaryLabel(
    automationType,
    intl,
    templateTitle,
    totalLevelLabel,
    sectionLevels,
    riskFilters,
    assessmentCategoryText,
    sourceFilterValue,
  )

  if (automationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL) {
    return trigger
  }

  let actionKey: string
  if (automationType === RequestAutomationType.AI_SCREEN) {
    actionKey = 'automatedSendout.screenForQuestionnaire'
  } else if (automationType === RequestAutomationType.AI_ENRICH) {
    actionKey = 'automatedSendout.enrichProperties'
  } else {
    actionKey = 'automatedSendout.sendQuestionnaire'
  }

  const action = formatMessage({ id: actionKey })
  return `${trigger} → ${action}`
}
